import React, { useMemo, useState } from "react";
import { MONTH_NAMES } from "../../lib/format";
import { DateCard } from "../DateCard";

export const CalendarView = ({ entries, onOpenDetail }) => {
  const [selectedMonth, setSelectedMonth] = useState(null);

  const byMonth = useMemo(() => {
    const map = {};
    for (let m = 1; m <= 12; m++) map[m] = [];
    map.movable = [];
    for (const e of entries) {
      if (e.month && e.month >= 1 && e.month <= 12) map[e.month].push(e);
      else map.movable.push(e);
    }
    return map;
  }, [entries]);

  const visibleEntries = selectedMonth
    ? byMonth[selectedMonth] || []
    : entries;

  if (entries.length === 0) {
    return (
      <div style={{ padding: "3rem 1rem", textAlign: "center", border: "1px dashed hsl(var(--line))" }}>
        <p style={{ fontFamily: "var(--font-display)", fontStyle: "italic" }}>No entries match these filters.</p>
      </div>
    );
  }

  return (
    <div data-testid="calendar-view">
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
        gap: 12,
        marginBottom: "2.5rem",
      }}>
        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
          const count = (byMonth[m] || []).length;
          const active = selectedMonth === m;
          return (
            <button
              key={m}
              type="button"
              onClick={() => setSelectedMonth((cur) => (cur === m ? null : m))}
              data-testid={`calendar-month-${m}`}
              aria-pressed={active}
              style={{
                textAlign: "left",
                padding: "1rem 1.1rem",
                background: active ? "hsl(var(--accent))" : "hsl(var(--bg-elev))",
                color: active ? "hsl(var(--accent-ink))" : "hsl(var(--ink))",
                border: `1px solid ${active ? "hsl(var(--accent))" : "hsl(var(--line))"}`,
                cursor: count ? "pointer" : "not-allowed",
                opacity: count ? 1 : 0.55,
                transition: "background-color 160ms ease, border-color 160ms ease, color 160ms ease",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
              disabled={count === 0}
            >
              <span style={{
                fontFamily: "var(--font-display)",
                fontSize: "1.05rem",
                fontWeight: 500,
              }}>
                {MONTH_NAMES[m]}
              </span>
              <span
                className="archival-figure"
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "1.85rem",
                  lineHeight: 1,
                  color: active ? "hsl(var(--accent-ink))" : "hsl(var(--accent))",
                  fontWeight: 500,
                }}
              >
                {count}
              </span>
              <span style={{
                fontSize: "0.7rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: active ? "hsl(var(--accent-ink) / 0.85)" : "hsl(var(--muted))",
              }}>
                {count === 1 ? "entry" : "entries"}
              </span>
            </button>
          );
        })}
      </div>

      {selectedMonth && (
        <header style={{
          display: "flex", alignItems: "baseline", justifyContent: "space-between",
          paddingBottom: "0.75rem", borderBottom: "1px solid hsl(var(--line))",
          marginBottom: "1.25rem", flexWrap: "wrap", gap: 12,
        }}>
          <h3 style={{ fontFamily: "var(--font-display)", fontSize: "1.5rem", fontWeight: 500, margin: 0 }}>
            {MONTH_NAMES[selectedMonth]}
          </h3>
          <button
            type="button"
            onClick={() => setSelectedMonth(null)}
            data-testid="calendar-show-all"
            style={{
              fontSize: "0.78rem", letterSpacing: "0.1em", textTransform: "uppercase",
              background: "none", border: "1px solid hsl(var(--line))", padding: "0.35rem 0.7rem",
              color: "hsl(var(--ink-soft))", cursor: "pointer",
            }}
          >
            Show all months
          </button>
        </header>
      )}

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: "1rem",
      }}>
        {visibleEntries.map((e) => (
          <DateCard key={e.id} entry={e} onOpen={onOpenDetail} variant="compact" />
        ))}
      </div>
    </div>
  );
};
